<template>
  <div class="min-h-screen relative overflow-hidden" :class="backgroundClass">
    <!-- White Top Bar -->
    <div class="fixed top-0 left-0 right-0 z-20 bg-white shadow-sm">
      <div class="flex items-center justify-center py-4 px-4">
        <img 
          src="/images/logo.png" 
          alt="Saskatoon Auto Connection" 
          class="h-14 w-auto object-contain"
          @error="handleTopBarImageError"
        />
      </div>
    </div>
    
    <!-- Liquid Background Animation -->
    <div class="liquid-background" :class="liquidAnimationClass"></div>
    
    <!-- Main Content Container -->
    <div class="relative z-10 min-h-screen flex flex-col items-center justify-center px-6" style="padding-top: 5rem; padding-bottom: 5rem;">
      
      <!-- Greeting Phase -->
      <div v-if="currentPhase === 'greeting'" class="text-center max-w-md mx-auto">
        <!-- Static Text -->
        <h1 class="text-3xl md:text-4xl font-bold text-white mb-8 leading-tight">
          <span class="caveat-greeting glow-text">Hello {{ customerName }}!</span><br>
          <span class="text-xl md:text-2xl font-medium mt-3 inline-block">How was your experience with us at Saskatoon Auto Connection?</span>
        </h1>
        
        <!-- Choice Buttons -->
        <div class="space-y-4 mt-12">
          <!-- Thumbs Up Button -->
          <button 
            @click="handlePositiveClick"
            class="choice-button choice-positive group w-full animate-float"
          >
            <div class="flex items-center justify-between px-2">
              <div class="flex items-center space-x-4">
                <div class="thumbs-icon">
                  <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                  </svg>
                </div>
                <span class="text-lg font-semibold">It was great!</span>
              </div>
              <div class="arrow-icon opacity-0 group-hover:opacity-100 transition-all duration-300">
                <svg class="w-6 h-6 arrow-animate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </button>
          
          <!-- Thumbs Down Button -->
          <button 
            @click="handleNegativeClick"
            class="choice-button choice-negative group w-full animate-float"
            :class="{ 'button-expanded': showConcernsComment }"
          >
            <div class="flex items-center justify-between px-2">
              <div class="flex items-center space-x-4">
                <div class="thumbs-icon">
                  <svg class="w-8 h-8" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M18 9.5a1.5 1.5 0 11-3 0v-6a1.5 1.5 0 013 0v6zM14 9.667v-5.43a2 2 0 00-1.106-1.79l-.05-.025A4 4 0 0011.055 2H5.64a2 2 0 00-1.962 1.608l-1.2 6A2 2 0 004.44 12H8v4a2 2 0 002 2 1 1 0 001-1v-.667a4 4 0 01.8-2.4l1.4-1.866a4 4 0 00.8-2.4z" />
                  </svg>
                </div>
                <div class="flex flex-col items-start">
                  <span class="text-lg font-semibold">I have concerns</span>
                  <span 
                    v-if="showConcernsComment" 
                    class="text-sm text-gray-600 mt-1 animate-fade-in text-left"
                  >
                    We'll help resolve this. Please tell us what happened.
                  </span>
                </div>
              </div>
              <div class="arrow-icon opacity-0 transition-all duration-300" 
                   :class="{ 'opacity-100': showConcernsComment }">
                <svg class="w-6 h-6 arrow-animate" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </button>
        </div>
      </div>

      <!-- Positive Feedback Phase -->
      <div v-if="currentPhase === 'positive'" class="text-center max-w-md mx-auto">
        <!-- Empty phase - user will be redirected immediately -->
      </div>

      <!-- Negative Feedback Phase -->
      <div v-if="currentPhase === 'negative'" class="text-center max-w-md mx-auto">
        <!-- Empty phase - user will be redirected immediately -->
      </div>
      
    </div>
    
    <!-- Confetti Container -->
    <div id="confetti-container" class="fixed inset-0 pointer-events-none z-50"></div>
    
    <!-- Copyright Footer -->
    <div class="fixed bottom-0 left-0 right-0 z-10 text-center pb-4">
      <p class="text-xs text-white text-opacity-60">
        © 2025 Saskatoon Auto Connection. All rights reserved.
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useFeedbackStore } from '@/stores/feedback'
import { getCustomerByName } from '@/services/api'

// Generate browser fingerprint for deduplication
const generateFingerprint = () => {
  return {
    userAgent: navigator.userAgent,
    language: navigator.language,
    languages: navigator.languages,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    screenResolution: `${screen.width}x${screen.height}`,
    screenDepth: screen.colorDepth,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    timezoneOffset: new Date().getTimezoneOffset(),
    touchSupport: 'ontouchstart' in window,
    timestamp: new Date().toISOString()
  }
}

// Track employee visit
const trackEmployeeVisit = async (employeeName: string, customerName: string, fingerprint?: any) => {
  try {
    console.log('🔧 Tracking employee visit:', { employeeName, customerName })
    const trackingFingerprint = fingerprint || generateFingerprint()
    
    const response = await fetch('/api/dashboard/simple-stats?employee=true', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        employeeName,
        customerName,
        type: 'service',
        action: 'visit',
        fingerprint: trackingFingerprint
      })
    })
    const result = await response.json()
    console.log('🔧 Employee visit tracking result:', result)
  } catch (error) {
    console.error('Failed to track employee visit:', error)
  }
}

// Track sales person visit
const trackSalesVisit = async (salesPersonName: string, customerName: string, fingerprint?: any) => {
  try {
    console.log('🏆 Tracking sales visit:', { salesPersonName, customerName })
    const trackingFingerprint = fingerprint || generateFingerprint()
    
    const response = await fetch('/api/dashboard/simple-stats?employee=true', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        employeeName: salesPersonName,
        customerName,
        type: 'sales',
        action: 'visit',
        fingerprint: trackingFingerprint
      })
    })
    const result = await response.json()
    console.log('🏆 Sales visit tracking result:', result)
  } catch (error) {
    console.error('Failed to track sales visit:', error)
  }
}

// Track employee feedback
const trackEmployeeFeedback = async (employeeName: string, customerName: string, feedbackType: 'positive' | 'negative' | 'neutral', employeeType: 'service' | 'sales') => {
  try {
    await fetch('/api/dashboard/simple-stats?employee=true', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        employeeName,
        customerName,
        type: employeeType,
        action: 'feedback',
        feedbackType
      })
    })
  } catch (error) {
    console.error('Failed to track employee feedback:', error)
  }
}

const router = useRouter()
const route = useRoute()
const store = useFeedbackStore()

// Phase management
const currentPhase = ref<'loading' | 'greeting' | 'positive' | 'negative'>('greeting')
const liquidAnimationClass = ref('liquid-to-dark')
const backgroundClass = ref('bg-gray-900')

// Concerns comment section
const showConcernsComment = ref(false)
const concernsText = ref('')

// Customer data
const customerName = computed(() => {
  const hasCustomerData = route.query.t as string
  if (hasCustomerData && store.customerName) {
    return store.customerName
  }
  return 'Valued Customer'
})

// Animation timers
let phaseTimer: ReturnType<typeof setTimeout> | null = null
let redirectTimer: ReturnType<typeof setTimeout> | null = null
let isRedirecting = false // Prevent multiple redirections

// Animation states (removed typewriter)
// const displayedGreeting = ref('')
// const displayedQuestion = ref('')
// const showButtons = ref(false)

// Handle image error
function handleImageError(event: Event) {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <h1 class="text-xl font-semibold text-white opacity-80">
        Saskatoon Auto Connection
      </h1>
    `
  }
}

// Handle top bar image error
function handleTopBarImageError(event: Event) {
  const img = event.target as HTMLImageElement
  const container = img.parentElement
  if (container) {
    container.innerHTML = `
      <h1 class="text-lg font-semibold text-gray-800">
        Saskatoon Auto Connection
      </h1>
    `
  }
}

// Phase transition functions
function startLiquidAnimation() {
  // Start with dark background immediately
  backgroundClass.value = 'bg-gray-900'
  liquidAnimationClass.value = 'liquid-to-dark'
  
  // Greeting is already shown from the start
}

// Handle positive button click
async function handlePositiveClick(event: Event) {
  console.log('🟢 Great button clicked!')
  const button = event.target as HTMLElement
  const buttonElement = button.closest('.choice-button') as HTMLElement
  
  // Check if button is already clicked - if so, redirect immediately
  if (buttonElement && buttonElement.classList.contains('button-clicked')) {
    console.log('🟢 Button already clicked - redirecting immediately to Google Reviews')
    redirectToGoogleReview()
    return
  }
  
  // First click - activate button and start timer
  console.log('🟢 First click - processing positive choice')
  handlePositiveChoice(event)
}

async function handlePositiveChoice(event: Event) {
  console.log('🟢 Positive choice - canceling any existing timer')
  // Cancel any existing redirect timer
  if (redirectTimer) {
    clearTimeout(redirectTimer)
  }
  
  // Hide concerns comment section if visible
  showConcernsComment.value = false
  
  // Remove any clicked states from all buttons first
  const allButtons = document.querySelectorAll('.choice-button')
  allButtons.forEach(btn => {
    btn.classList.remove('button-clicked')
  })
  
  // Add button clicked state to positive button
  const button = event.target as HTMLElement
  const buttonElement = button.closest('.choice-button') as HTMLElement
  if (buttonElement) {
    buttonElement.classList.add('button-clicked')
  }
  
  // Trigger confetti explosion from button!
  createConfettiExplosion(event.target as HTMLElement)
  
  // Animate to bright background
  backgroundClass.value = 'bg-green-600'
  liquidAnimationClass.value = 'liquid-to-bright'
  
  // Submit positive feedback to database BEFORE redirecting
  try {
    console.log('📊 Starting positive feedback submission...')
    console.log('📊 Customer data:', store.customer)
    const response = await submitPositiveFeedback()
    console.log('📊 Positive feedback submission completed successfully')
    
    // Store case number for later use
    if (response && response.caseNumber) {
      store.setCaseNumber(response.caseNumber)
      console.log('✅ Case number stored:', response.caseNumber)
    }
  } catch (error) {
    console.error('❌ Error submitting positive feedback:', error)
    console.error('❌ Error details:', error)
    // Continue with redirect even if submission fails
  }
  
  // Redirect immediately to Google Reviews (no delay)
  console.log('🟢 Redirecting immediately to Google Reviews')
  redirectToGoogleReview()
}

// Handle negative button click
function handleNegativeClick(event: Event) {
  const button = event.target as HTMLElement
  const buttonElement = button.closest('.choice-button') as HTMLElement
  
  // Check if concerns message is already showing - if so, redirect immediately
  if (showConcernsComment.value) {
    console.log('🔴 Concerns message already showing - redirecting immediately to complaint page')
    redirectToComplaint()
    return
  }
  
  // First click - activate button and start timer
  handleNegativeChoice(event)
}

function handleNegativeChoice(event: Event) {
  console.log('🔴 Negative choice - canceling any existing timer')
  // Cancel any existing redirect timer
  if (redirectTimer) {
    clearTimeout(redirectTimer)
  }
  
  // Remove any clicked states from all buttons first
  const allButtons = document.querySelectorAll('.choice-button')
  allButtons.forEach(btn => {
    btn.classList.remove('button-clicked')
  })
  
  // Add button clicked state for concerns button
  const button = event.target as HTMLElement
  const buttonElement = button.closest('.choice-button') as HTMLElement
  if (buttonElement) {
    buttonElement.classList.add('button-clicked')
  }
  
  // Show concerns comment section inside button
  showConcernsComment.value = true
  
  // Wait 5 seconds then redirect directly to complaint page
  redirectTimer = setTimeout(() => {
    console.log('🔴 5 seconds passed - redirecting to complaint page')
    redirectToComplaint()
  }, 5000)
  
  // Animate to dark red background
  backgroundClass.value = 'bg-red-900'
  liquidAnimationClass.value = 'liquid-to-red'
}

// New handlers for 2-step process
function handleGoogleReviewClick() {
  // This is the second click - redirect to Google Reviews
  redirectToGoogleReview()
}

function handleComplaintClick() {
  console.log('📝 Second click: Share Your Concerns - redirecting to complaint page')
  // This is the second click - redirect to complaint page
  redirectToComplaint()
}

// Handle concerns submission
function submitConcerns() {
  if (!concernsText.value.trim()) return
  
  console.log('📝 Submitting concerns:', concernsText.value)
  
  // Set feedback data with the concerns text
  const feedback = {
    text: concernsText.value,
    rating: 2,
    sentiment: 'negative', 
    category: 'NEGATIVE',
    confidence: 1.0
  }
  store.setFeedback(feedback)
  
  // Navigate to complaint page
  router.push('/complaint')
}

async function redirectToGoogleReview() {
  console.log('🌟 Determining Google review URL based on sales/service parameter...')
  
  // Prevent multiple redirections
  if (isRedirecting) {
    console.log('🚫 Already redirecting - ignoring duplicate call')
    return
  }
  isRedirecting = true
  
  // Cancel any existing timer to prevent additional calls
  if (redirectTimer) {
    clearTimeout(redirectTimer)
    redirectTimer = null
  }
  
  // Set positive feedback data
  const feedback = {
    text: 'Customer selected positive feedback',
    rating: 5,
    sentiment: 'positive',
    category: 'POSITIVE',
    confidence: 1.0
  }
  store.setFeedback(feedback)
  
  // Case number should already be set from handlePositiveClick
  // No need to submit again here
  
  // Determine which Google review URL to use
  let googleReviewUrl = 'https://g.page/r/CbWWXBd9rLXXEAE/review'  // Service Center Google Review
  
  // Check if this came from a sales person URL
  if (store.customer.salesPersonName) {
    googleReviewUrl = 'https://g.page/r/CYtn30uN7-zZEAI/review'  // Sales Google Review
    console.log('🏆 SALES PERSON DETECTED - Redirecting to Sales Google Review for:', store.customer.salesPersonName)
  } else {
    console.log('🔧 DEFAULT - Redirecting to Service Center Google Review')
  }
  
  // Send email notification before redirecting
  try {
    console.log('📧 Sending email notification before redirect...')
    const emailData = {
      customer: store.customer,
      feedback: {
        text: 'Customer selected positive feedback',
        rating: 5,
        sentiment: 'positive'
      },
      type: 'positive'
    }
    
    fetch('/api/feedback/send-email', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailData)
    }).then(response => {
      console.log('📧 Email notification sent, status:', response.status)
    }).catch(error => {
      console.error('📧 Failed to send email notification:', error)
    })
  } catch (error) {
    console.error('📧 Error sending email notification:', error)
  }
  
  // Redirect directly to Google Reviews
  console.log('🚀 Redirecting directly to Google Reviews:', googleReviewUrl)
  window.location.href = googleReviewUrl
}

function redirectToComplaint() {
  console.log('🔥 redirectToComplaint() called - navigating to /complaint')
  // Set negative feedback data
  const feedback = {
    text: 'Customer selected negative feedback',
    rating: 2,
    sentiment: 'negative', 
    category: 'NEGATIVE',
    confidence: 1.0
  }
  store.setFeedback(feedback)
  
  // Navigate to complaint page
  router.push('/complaint')
}

// Helper function to decode service name from URL
function decodeServiceName(serviceName: string): string {
  const decoded = decodeURIComponent(serviceName)
  return decoded
    .replace(/-/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

// Confetti explosion effect from button center
function createConfettiExplosion(button: HTMLElement) {
  console.log('🎉 Creating confetti explosion from button!')
  const container = document.getElementById('confetti-container')
  if (!container || !button) {
    console.error('❌ Confetti container or button not found!')
    return
  }
  
  // Get button position
  const buttonRect = button.getBoundingClientRect()
  const centerX = buttonRect.left + buttonRect.width / 2
  const centerY = buttonRect.top + buttonRect.height / 2
  
  const colors = ['#ff6b6b', '#4ecdc4', '#45b7d1', '#96ceb4', '#feca57', '#ff9ff3', '#54a0ff']
  const confettiCount = 60
  
  // Create explosion burst pattern (straight upward)
  for (let i = 0; i < confettiCount; i++) {
    // Focus explosion straight upward (angles between -135° to -45° = upward cone)
    const baseAngle = -135 + (i * 90 / confettiCount) // Spread in narrow upward cone
    const angle = baseAngle + Math.random() * 30 - 15 // Add some randomness
    createConfettiBurst(container, colors[Math.floor(Math.random() * colors.length)], centerX, centerY, angle)
  }
  
  // Clean up confetti after animation
  setTimeout(() => {
    container.innerHTML = ''
  }, 4000)
}

function createConfettiBurst(container: HTMLElement, color: string, centerX: number, centerY: number, angle: number) {
  const confetti = document.createElement('div')
  const size = Math.random() * 16 + 8
  const duration = Math.random() * 1.5 + 2
  const speed = Math.random() * 200 + 150 // Explosion speed
  const gravity = 300 // Falling speed
  
  confetti.style.position = 'absolute'
  confetti.style.width = size + 'px'
  confetti.style.height = size + 'px'
  confetti.style.backgroundColor = color
  confetti.style.left = centerX + 'px'
  confetti.style.top = centerY + 'px'
  confetti.style.borderRadius = '0px' // Perfect square particles
  confetti.style.opacity = '1'
  confetti.style.pointerEvents = 'none'
  confetti.style.zIndex = '9999'
  confetti.style.boxShadow = `0 0 6px ${color}`
  
  // Convert angle to radians
  const radian = (angle * Math.PI) / 180
  
  // Calculate initial velocity (negative Y for upward launch)
  let velocityX = Math.cos(radian) * speed
  let velocityY = Math.sin(radian) * speed - 200 // Add upward boost to overcome gravity
  let currentX = 0
  let currentY = 0
  
  let startTime: number | null = null
  const animate = (timestamp: number) => {
    if (!startTime) startTime = timestamp
    const elapsed = (timestamp - startTime) / 1000 // Convert to seconds
    
    if (elapsed < duration) {
      // Physics calculation
      currentX = velocityX * elapsed
      currentY = velocityY * elapsed + 0.5 * gravity * elapsed * elapsed // Add gravity
      
      const rotation = 0 // No rotation for square particles
      const scale = Math.max(0.2, 1 - elapsed / duration) // Shrink over time
      const opacity = Math.max(0, 1 - elapsed / duration) // Fade out
      
      confetti.style.left = (centerX + currentX) + 'px'
      confetti.style.top = (centerY + currentY) + 'px'
      confetti.style.transform = `rotate(${rotation}deg) scale(${scale})`
      confetti.style.opacity = opacity.toString()
      
      requestAnimationFrame(animate)
    } else {
      confetti.remove()
    }
  }
  
  container.appendChild(confetti)
  requestAnimationFrame(animate)
}

// Helper function to get today's date
function getTodaysDate(): string {
  const today = new Date()
  const options: Intl.DateTimeFormatOptions = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  }
  return today.toLocaleDateString('en-US', options)
}

onMounted(async () => {
  // Generate browser fingerprint once for session
  const sessionFingerprint = generateFingerprint()
  
  // Track visitor with browser fingerprinting for deduplication
  try {
    
    console.log('🔍 Browser fingerprint generated for visitor deduplication')
    
    const response = await fetch('/api/dashboard/simple-stats?visitor=true', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        fingerprint: sessionFingerprint,
        url: window.location.href,
        referrer: document.referrer
      })
    })
    
    if (response.ok) {
      const result = await response.json()
      console.log('📊 Visitor tracked successfully:', {
        isNewVisitor: result.isNewVisitor,
        isNewDailyVisitor: result.isNewDailyVisitor,
        totalVisitors: result.total,
        todayVisitors: result.today
      })
    } else {
      console.error('📊 Visitor tracking failed:', response.status)
    }
  } catch (error) {
    console.error('📊 Failed to track visitor:', error)
  }

  // Load customer data
  const customerToken = route.query.t as string
  const carToken = route.query.c as string
  const employeeParam = route.query.emp as string // Check if URL came from employee link
  const salesParam = route.query.sales as string // Check if URL came from sales person link
  
  // Debug: log all parameters
  console.log('🔍 FeedbackForm DEBUG - URL Parameters:')
  console.log('  customerToken (t):', customerToken)
  console.log('  carToken (c):', carToken)
  console.log('  employeeParam (emp):', employeeParam)
  console.log('  salesParam (sales):', salesParam)
  
  if (customerToken) {
    try {
      const customer = await getCustomerByName(customerToken)
      if (customer) {
        if (carToken) {
          customer.car = decodeServiceName(carToken)
        }
        customer.date = getTodaysDate()
        
        // Check if this came from an employee URL
        if (employeeParam && employeeParam !== customerToken) {
          console.log('🔧 Employee param detected:', employeeParam, 'for customer:', customerToken)
          customer.employeeName = employeeParam
          await trackEmployeeVisit(employeeParam, customerToken, sessionFingerprint)
          console.log('🔧 Employee visit tracked for:', employeeParam)
        }
        
        // Check if this came from a sales person URL
        if (salesParam && salesParam !== customerToken) {
          console.log('🏆 Sales param detected:', salesParam, 'for customer:', customerToken)
          customer.salesPersonName = salesParam
          await trackSalesVisit(salesParam, customerToken, sessionFingerprint)
          console.log('🏆 Sales visit tracked for:', salesParam)
        }
        
        store.setCustomer(customer)
      } else {
        const carInfo = carToken ? decodeServiceName(carToken) : ''
        const customerData = {
          name: customerToken.charAt(0).toUpperCase() + customerToken.slice(1),
          service: '',
          car: carInfo,
          date: getTodaysDate()
        }
        
        // Check if this came from an employee URL
        if (employeeParam && employeeParam !== customerToken) {
          customerData.employeeName = employeeParam
          await trackEmployeeVisit(employeeParam, customerToken, sessionFingerprint)
        }
        
        // Check if this came from a sales person URL
        if (salesParam && salesParam !== customerToken) {
          customerData.salesPersonName = salesParam
          await trackSalesVisit(salesParam, customerToken, sessionFingerprint)
        }
        
        store.setCustomer(customerData)
      }
    } catch (err) {
      console.error('Failed to load customer info:', err)
      const carInfo = carToken ? decodeServiceName(carToken) : ''
      const customerData = {
        name: customerToken.charAt(0).toUpperCase() + customerToken.slice(1),
        service: '',
        car: carInfo,
        date: getTodaysDate()
      }
      
      // Check if this came from an employee URL
      if (employeeParam && employeeParam !== customerToken) {
        customerData.employeeName = employeeParam
        await trackEmployeeVisit(employeeParam, customerToken, sessionFingerprint)
      }
      
      // Check if this came from a sales person URL
      if (salesParam && salesParam !== customerToken) {
        customerData.salesPersonName = salesParam
        await trackSalesVisit(salesParam, customerToken, sessionFingerprint)
      }
      
      store.setCustomer(customerData)
    }
  } else if (employeeParam) {
    // Direct employee URL access without customer token
    const customerData = {
      name: '',
      service: '',
      car: '',
      date: getTodaysDate(),
      employeeName: employeeParam
    }
    
    await trackEmployeeVisit(employeeParam, '', sessionFingerprint)
    store.setCustomer(customerData)
  } else if (salesParam) {
    // Direct sales person URL access without customer token
    console.log('🏆 SALES PARAM DETECTED - Setting up sales customer data:', salesParam)
    const customerData = {
      name: '',
      service: '',
      car: '',
      date: getTodaysDate(),
      salesPersonName: salesParam
    }
    
    console.log('🏆 Customer data for sales:', customerData)
    await trackSalesVisit(salesParam, '', sessionFingerprint)
    store.setCustomer(customerData)
    console.log('🏆 Store updated with sales data')
  }
  
  // Start the liquid animation sequence
  startLiquidAnimation()
})

// Submit positive feedback to database
async function submitPositiveFeedback() {
  try {
    const customerData = {
      name: customerName.value,
      service: store.customer.service || '',
      date: store.customer.date || new Date().toLocaleDateString(),
      car: store.customer.car || '',
      employeeName: store.customer.employeeName || undefined,
      employeeId: store.customer.employeeId || undefined,
      salesPersonName: store.customer.salesPersonName || undefined
    }

    const feedbackData = {
      rating: 5,  // Great button = 5 stars
      text: 'Great experience!',
      sentiment: 'positive' as const
    }

    console.log('📊 Sending positive feedback data:', { customer: customerData, feedback: feedbackData })

    const response = await fetch('/api/submitPositive', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer: customerData,
        feedback: feedbackData
      })
    })

    if (response.ok) {
      const result = await response.json()
      console.log('✅ Positive feedback submitted successfully:', result)
      
      // Track employee feedback if employee or sales person is associated
      if (customerData.employeeName) {
        await trackEmployeeFeedback(customerData.employeeName, customerData.name, 'positive', 'service')
      }
      if (customerData.salesPersonName) {
        await trackEmployeeFeedback(customerData.salesPersonName, customerData.name, 'positive', 'sales')
      }
      
      return result
    } else {
      const error = await response.text()
      console.error('❌ Failed to submit positive feedback:', error)
      throw new Error(`HTTP ${response.status}: ${error}`)
    }
  } catch (error) {
    console.error('❌ Error in submitPositiveFeedback:', error)
    throw error
  }
}

onUnmounted(() => {
  if (phaseTimer) {
    clearTimeout(phaseTimer)
  }
  if (redirectTimer) {
    clearTimeout(redirectTimer)
  }
})
</script>

<style scoped>
/* Liquid Background Animation */
.liquid-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 30% 50%, rgba(0, 0, 0, 0.8) 0%, transparent 50%),
              radial-gradient(circle at 70% 80%, rgba(0, 0, 0, 0.6) 0%, transparent 50%),
              radial-gradient(circle at 50% 20%, rgba(0, 0, 0, 0.7) 0%, transparent 50%);
  opacity: 0;
  transition: all 1.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.liquid-initial {
  opacity: 0;
  transform: scale(0.8);
}

.liquid-to-dark {
  opacity: 1;
  transform: scale(1.2);
  background: radial-gradient(circle at 30% 50%, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.7) 60%, transparent 100%),
              radial-gradient(circle at 70% 80%, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.5) 60%, transparent 100%),
              radial-gradient(circle at 50% 20%, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.6) 60%, transparent 100%);
}

.liquid-to-bright {
  opacity: 0.8;
  transform: scale(1.1);
  background: radial-gradient(circle at 40% 60%, rgba(34, 197, 94, 0.4) 0%, transparent 70%),
              radial-gradient(circle at 60% 40%, rgba(34, 197, 94, 0.3) 0%, transparent 70%);
}

.liquid-to-darker {
  opacity: 0.9;
  transform: scale(1.1);
  background: radial-gradient(circle at 40% 60%, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 70%),
              radial-gradient(circle at 60% 40%, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0.3) 70%);
}

.liquid-to-red {
  opacity: 0.8;
  transform: scale(1.1);
  background: radial-gradient(circle at 40% 60%, rgba(127, 29, 29, 0.8) 0%, rgba(127, 29, 29, 0.4) 70%),
              radial-gradient(circle at 60% 40%, rgba(153, 27, 27, 0.7) 0%, rgba(153, 27, 27, 0.3) 70%);
}

/* Background transitions */
.bg-gray-500 {
  background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
  transition: all 1.5s ease;
}

.bg-gray-900 {
  background: linear-gradient(135deg, #111827 0%, #1f2937 50%, #374151 100%);
  transition: all 1.5s ease;
}

.bg-green-600 {
  background: 
    radial-gradient(circle at 20% 80%, #10b981 0%, #22c55e 30%, #34d399 100%),
    linear-gradient(135deg, #16a34a 0%, #22c55e 50%, #4ade80 100%);
  transition: all 2s cubic-bezier(0.4, 0, 0.2, 1);
  animation: greenWaveFlow 4s ease-in-out infinite;
}

.bg-gray-800 {
  background: linear-gradient(135deg, #1f2937 0%, #374151 50%, #4b5563 100%);
  transition: all 1.5s ease;
}

.bg-red-900 {
  background: linear-gradient(135deg, #7f1d1d 0%, #991b1b 50%, #b91c1c 100%);
  transition: all 1.5s ease;
}

/* Choice Buttons */
.choice-button {
  @apply bg-white rounded-2xl p-6 shadow-lg transform transition-all duration-300;
  @apply hover:scale-105 hover:shadow-xl;
  @apply active:scale-95;
  color: #374151;
  border: 2px solid transparent;
}

/* Expanded button state */
.button-expanded {
  min-height: auto;
  transition: all 0.5s ease;
}

/* Elegant Dissolve + Float-up Entrance */
@keyframes elegantEntrance {
  0% {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
    filter: blur(10px);
  }
  60% {
    opacity: 0.8;
    filter: blur(2px);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0px);
  }
}

.elegant-entrance {
  opacity: 0;
  animation: elegantEntrance 1.2s cubic-bezier(0.2, 0.8, 0.2, 1) both;
}

/* Premium fade-in with depth */
@keyframes premiumFadeIn {
  0% {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
    filter: blur(5px) brightness(0.8);
  }
  50% {
    opacity: 0.7;
    filter: blur(1px) brightness(0.9);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0px) brightness(1);
  }
}

.choice-positive:hover {
  border-color: #22c55e;
  background: linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%);
  transform: scale(1.05) translateY(-2px);
  box-shadow: 0 20px 40px rgba(34, 197, 94, 0.2);
}

.choice-negative:hover {
  border-color: #f97316;
  background: linear-gradient(135deg, #ffffff 0%, #fef3f2 100%);
  transform: scale(1.05) translateY(-2px);
  box-shadow: 0 20px 40px rgba(249, 115, 22, 0.2);
}

/* Hide arrow icon by default for negative button */
.choice-negative .arrow-icon {
  opacity: 0 !important;
}

.choice-positive:active {
  transform: scale(0.98) translateY(1px);
  box-shadow: 0 10px 20px rgba(34, 197, 94, 0.3);
}

/* Button clicked state - change thumbs icon color */
.button-clicked.choice-positive .thumbs-icon svg {
  color: #22c55e !important;
  transition: color 0.5s ease;
}

/* Only show colored thumb icon when concerns button is clicked */
.button-clicked.choice-negative .thumbs-icon svg {
  color: #f97316 !important;
  transition: color 0.5s ease;
}

/* Show colored thumb icon when concerns comment is visible */
.choice-negative .thumbs-icon svg {
  color: #374151;
  transition: color 0.5s ease;
}

.choice-negative.button-expanded .thumbs-icon svg {
  color: #f97316 !important;
  transition: color 0.5s ease;
}

/* Arrow animation only when button is clicked */
.button-clicked .arrow-icon {
  opacity: 1 !important;
  animation: arrowPulse 0.8s ease-in-out infinite;
}

.button-clicked .arrow-animate {
  animation: arrowBounce 1.5s ease-in-out infinite;
}

/* Arrow animation when concerns comment is shown */
.choice-negative.button-expanded .arrow-icon {
  opacity: 1 !important;
  animation: arrowPulse 0.8s ease-in-out infinite;
}

.choice-negative.button-expanded .arrow-animate {
  animation: arrowBounce 1.5s ease-in-out infinite;
}

/* Arrow click signal animation (only active when clicked) */
@keyframes arrowPulse {
  0%, 100% {
    transform: translateX(0) scale(1);
    opacity: 1;
  }
  50% {
    transform: translateX(4px) scale(1.1);
    opacity: 0.8;
  }
}

@keyframes arrowBounce {
  0%, 100% {
    transform: translateX(0);
  }
  25% {
    transform: translateX(3px);
  }
  75% {
    transform: translateX(-2px);
  }
}

/* Default arrow state - no animation */
.arrow-animate {
  transition: all 0.3s ease;
}

.choice-negative:active {
  transform: scale(0.98) translateY(1px);
  box-shadow: 0 10px 20px rgba(249, 115, 22, 0.3);
}

.thumbs-icon {
  @apply transform transition-transform duration-300;
}

.choice-button:hover .thumbs-icon {
  @apply scale-110;
}

/* Review Button */
.review-button {
  @apply bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl px-8 py-4;
  @apply border border-white border-opacity-30 text-white;
  @apply transform transition-all duration-300 hover:scale-105;
  @apply hover:bg-opacity-30 hover:border-opacity-50;
  @apply shadow-lg hover:shadow-xl;
}

/* Complaint Button */
.complaint-button {
  @apply bg-orange-500 bg-opacity-20 backdrop-blur-lg rounded-2xl px-8 py-4;
  @apply border border-orange-400 border-opacity-30 text-white;
  @apply transform transition-all duration-300 hover:scale-105;
  @apply hover:bg-opacity-30 hover:border-opacity-50;
  @apply shadow-lg hover:shadow-xl;
}

/* Animations */
.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

.animate-slide-up {
  animation: slideUp 1s ease-out;
}

.animate-scale-in {
  animation: scaleIn 0.8s ease-out;
}

.animate-bounce-gentle {
  animation: bounceGentle 2s ease-in-out infinite;
}

.animate-pulse-gentle {
  animation: pulseGentle 2s ease-in-out infinite;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes bounceGentle {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes pulseGentle {
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

/* Mobile responsiveness */
@media (max-width: 768px) {
  .choice-button {
    @apply p-5;
  }
  
  .thumbs-icon svg {
    @apply w-7 h-7;
  }
  
  h1 {
    @apply text-2xl;
  }
  
  h1 span {
    @apply text-lg;
  }
  
  .review-button,
  .complaint-button {
    @apply px-6 py-3;
  }
  
  /* Responsive sizing for Caveat font */
  .caveat-greeting {
    font-size: 2.2em;
  }
}

@media (max-width: 480px) {
  .caveat-greeting {
    font-size: 1.8em;
  }
}

@media (max-width: 360px) {
  .caveat-greeting {
    font-size: 1.5em;
  }
}

/* Caveat Font Style */
.caveat-greeting {
  font-family: "Caveat", cursive;
  font-optical-sizing: auto;
  font-weight: 600;
  font-style: normal;
  font-size: 2.375em; /* 5% smaller than 2.5em */
  line-height: 1.1;
  display: inline-block;
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

/* Staggered Fade-in Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.animate-fade-in-up {
  animation: fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) both;
}

/* Enhanced text glow for elegant entrance */
.elegant-entrance.glow-text {
  text-shadow: 
    0 0 5px rgba(255, 255, 255, 0.4),
    0 0 15px rgba(255, 255, 255, 0.3),
    0 0 25px rgba(255, 255, 255, 0.2),
    0 0 35px rgba(255, 255, 255, 0.1);
}

/* Floating Animation */
@keyframes float {
  0%, 100% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-6px);
  }
}

.animate-float {
  animation: float 3s ease-in-out infinite;
}

.animate-float:nth-child(2) {
  animation-delay: 1.5s;
}

/* Text Glow Effect */
.glow-text {
  text-shadow: 
    0 0 10px rgba(255, 255, 255, 0.3),
    0 0 20px rgba(255, 255, 255, 0.2),
    0 0 30px rgba(255, 255, 255, 0.1);
}

/* Enhanced Micro-interactions */
.choice-button {
  position: relative;
  overflow: hidden;
}

.choice-button::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transition: left 0.5s;
}

.choice-button:hover::before {
  left: 100%;
}

/* Pulse effect on load */
@keyframes pulse-glow {
  0%, 100% {
    box-shadow: 0 0 5px rgba(255, 255, 255, 0.1);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 255, 255, 0.2), 0 0 30px rgba(255, 255, 255, 0.1);
  }
}

.choice-button {
  animation: pulse-glow 2s ease-in-out infinite;
  animation-delay: 2s;
}

/* Liquid Flow Animations */
@keyframes liquidFlow {
  0% {
    background-position: 0% 50%, 100% 50%, 50% 0%;
    transform: scale(1.1);
  }
  25% {
    background-position: 30% 60%, 80% 40%, 60% 20%;
    transform: scale(1.15) rotate(0.5deg);
  }
  50% {
    background-position: 60% 40%, 50% 70%, 40% 60%;
    transform: scale(1.2) rotate(0deg);
  }
  75% {
    background-position: 80% 30%, 20% 60%, 70% 80%;
    transform: scale(1.18) rotate(-0.3deg);
  }
  100% {
    background-position: 100% 50%, 0% 50%, 50% 100%;
    transform: scale(1.2);
  }
}

@keyframes greenWaveFlow {
  0%, 100% {
    background-position: 0% 50%, 0% 50%;
    filter: hue-rotate(0deg) saturate(1) brightness(1);
  }
  25% {
    background-position: 25% 75%, 25% 25%;
    filter: hue-rotate(5deg) saturate(1.1) brightness(1.05);
  }
  50% {
    background-position: 50% 100%, 50% 50%;
    filter: hue-rotate(10deg) saturate(1.2) brightness(1.1);
  }
  75% {
    background-position: 75% 25%, 75% 75%;
    filter: hue-rotate(5deg) saturate(1.1) brightness(1.05);
  }
}

/* Enhanced liquid morphing effect */
.liquid-background.liquid-to-bright {
  background-size: 400% 400%, 300% 300%, 350% 350%;
  animation: liquidFlow 3s ease-in-out;
}

/* Smooth color wave transition */
@keyframes colorWave {
  0% {
    background-position: -200% center;
  }
  100% {
    background-position: 200% center;
  }
}

/* Confetti Animation */
@keyframes confettiFall {
  0% {
    transform: translateY(-10px) rotate(0deg) scale(1);
    opacity: 1;
  }
  10% {
    transform: translateY(10vh) rotate(36deg) scale(0.9);
  }
  20% {
    transform: translateY(20vh) rotate(72deg) scale(0.8);
  }
  30% {
    transform: translateY(30vh) rotate(108deg) scale(0.9);
  }
  40% {
    transform: translateY(40vh) rotate(144deg) scale(0.7);
  }
  50% {
    transform: translateY(50vh) rotate(180deg) scale(0.8);
  }
  60% {
    transform: translateY(60vh) rotate(216deg) scale(0.6);
  }
  70% {
    transform: translateY(70vh) rotate(252deg) scale(0.7);
  }
  80% {
    transform: translateY(80vh) rotate(288deg) scale(0.5);
  }
  90% {
    transform: translateY(90vh) rotate(324deg) scale(0.6);
  }
  100% {
    transform: translateY(100vh) rotate(360deg) scale(0.3);
    opacity: 0;
  }
}

/* Confetti explosion burst */
@keyframes confettiBurst {
  0% {
    transform: scale(0) rotate(0deg);
    opacity: 0;
  }
  10% {
    transform: scale(1.2) rotate(36deg);
    opacity: 1;
  }
  100% {
    transform: scale(0.3) rotate(360deg);
    opacity: 0;
  }
}

/* Touch optimization */
.choice-button,
.review-button,
.complaint-button {
  touch-action: manipulation;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0.1);
  min-height: 48px;
}
</style>